import React from 'react';
import type { FactoryComparison } from '../../types/buildExpansion';

/**
 * Factory vs current spec comparison. Side-by-side table on md and up,
 * stacked rows under 768px. Changed rows are highlighted neutrally with an
 * accent border and weight; a modified car is a fact, not a pass/fail.
 */

export interface FactoryCurrentComparisonProps {
  comparison: FactoryComparison;
  className?: string;
}

export const FactoryCurrentComparison: React.FC<FactoryCurrentComparisonProps> = ({
  comparison,
  className = '',
}) => {
  if (!comparison || comparison.rows.length === 0) return null;

  const footnotes: string[] = [];
  if (comparison.is_estimated) footnotes.push('Factory data estimated.');
  if (comparison.source) footnotes.push(`Source: ${comparison.source}`);

  return (
    <section className={`card p-5 sm:p-6 ${className}`} aria-label="Factory versus current specs">
      <h3 className="text-sm font-semibold text-white">Factory vs current</h3>

      {/* Desktop and tablet: side-by-side table */}
      <div className="hidden md:block mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">
            Factory versus current specifications. Changed rows are marked.
          </caption>
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th scope="col" className="text-left text-meta font-medium py-2 pr-4">
                Spec
              </th>
              <th scope="col" className="text-left text-meta font-medium py-2 pr-4">
                Factory
              </th>
              <th scope="col" className="text-left text-meta font-medium py-2">
                Current
              </th>
            </tr>
          </thead>
          <tbody>
            {comparison.rows.map((row) => (
              <tr
                key={row.key}
                className={`border-b border-white/[0.06] last:border-b-0 ${
                  row.changed ? 'bg-white/[0.03]' : ''
                }`}
              >
                <th
                  scope="row"
                  className={`text-left font-normal text-gray-400 py-2.5 pr-4 pl-2 ${
                    row.changed ? 'border-l-2 border-orange-400/60' : 'border-l-2 border-transparent'
                  }`}
                >
                  {row.label}
                </th>
                <td className="py-2.5 pr-4 text-gray-400">{row.factory || '-'}</td>
                <td className={`py-2.5 ${row.changed ? 'text-white font-medium' : 'text-gray-300'}`}>
                  {row.current || '-'}
                  {row.changed && <span className="sr-only"> (changed from factory)</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phone: stacked rows */}
      <ul className="md:hidden mt-3 space-y-2 list-none">
        {comparison.rows.map((row) => (
          <li
            key={row.key}
            className={`rounded-lg border px-3 py-2.5 ${
              row.changed
                ? 'border-orange-400/40 bg-white/[0.03]'
                : 'border-white/[0.06] bg-[#0c0c0c]/50'
            }`}
          >
            <p className="text-meta">
              {row.label}
              {row.changed && <span className="sr-only"> (changed from factory)</span>}
            </p>
            <dl className="mt-1.5 space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Factory</dt>
                <dd className="text-gray-400 text-right">{row.factory || '-'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Current</dt>
                <dd className={`text-right ${row.changed ? 'text-white font-medium' : 'text-gray-300'}`}>
                  {row.current || '-'}
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>

      {footnotes.length > 0 && (
        <p className="text-xs text-gray-500 mt-3">{footnotes.join(' ')}</p>
      )}
    </section>
  );
};

export default FactoryCurrentComparison;
